import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney, startViewTransition } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Updates the section
   */
  #updateSection() {
    const viewTransition = !this.closest('dialog');

    if (viewTransition) {
      startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']);
    } else {
      sectionRenderer.renderSection(this.sectionId);
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * @typedef {Object} PriceRangeFacetRefs
 * @property {HTMLInputElement} minRange - The minimum price range slider
 * @property {HTMLInputElement} maxRange - The maximum price range slider
 * @property {HTMLInputElement} minInput - The hidden minimum price input
 * @property {HTMLInputElement} maxInput - The hidden maximum price input
 * @property {HTMLElement} minValueDisplay - The minimum value display element
 * @property {HTMLElement} maxValueDisplay - The maximum value display element
 * @property {HTMLElement} sliderRange - The visual range bar element
 * @property {HTMLElement} progressMin - The min progress indicator element
 * @property {HTMLElement} progressMax - The max progress indicator element
 */

/**
 * Handles price range facet functionality with sliders
 * @extends {Component<PriceRangeFacetRefs>}
 */
class PriceRangeFacetComponent extends Component {
  /** @type {MutationObserver | null} */
  #mutationObserver;
  /** @type {number | null} */
  #updateTimeoutId = null;
  /** @type {number | null} */
  #inputTimeoutId = null;
  /** @type {boolean} */
  #isUpdating = false;

  connectedCallback() {
    super.connectedCallback();
    console.log('PriceRangeFacetComponent: connectedCallback started', {
      hasRefs: {
        minRange: !!this.refs.minRange,
        maxRange: !!this.refs.maxRange,
        sliderRange: !!this.refs.sliderRange
      },
      rangeMax: this.dataset.rangeMax
    });
    
    this.#setupRangeSliders();
    this.#setupMutationObserver();
    
    // Listen for filter updates (which trigger section re-renders)
    // This covers ALL filter types: availability, tags, vendors, Search & Discovery app filters, etc.
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    
    // Also listen for any form changes that might not trigger FilterUpdate event
    // This is especially important for Search & Discovery app filters
    const form = this.closest('form');
    if (form) {
      form.addEventListener('change', this.#handleFormChange);
      form.addEventListener('input', this.#handleFormInput);
    }
    
    // Use multiple methods to ensure slider range is updated properly
    // This handles both initial load and AJAX re-renders
    this.#ensureSliderRangeUpdate();
    
    // Add a final safety check after a longer delay to catch any edge cases
    setTimeout(() => {
      if (this.isConnected) {
        console.log('PriceRangeFacetComponent: Final safety check for slider range visibility');
        this.#updateSliderRange();
      }
    }, 500);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#mutationObserver instanceof MutationObserver) {
      this.#mutationObserver.disconnect();
    }
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    
    // Remove form event listeners
    const form = this.closest('form');
    if (form) {
      form.removeEventListener('change', this.#handleFormChange);
      form.removeEventListener('input', this.#handleFormInput);
    }
    
    // Clear any pending timeouts
    if (this.#updateTimeoutId !== null) {
      clearTimeout(this.#updateTimeoutId);
      this.#updateTimeoutId = null;
    }
    if (this.#inputTimeoutId !== null) {
      clearTimeout(this.#inputTimeoutId);
      this.#inputTimeoutId = null;
    }
    this.#isUpdating = false;
  }

  /**
   * Handles filter updates to ensure slider range stays visible after re-renders
   * This works with ANY filter type: availability, tags, vendors, Search & Discovery app filters, etc.
   */
  #handleFilterUpdate = (/** @type {FilterUpdateEvent} */ event) => {
    console.log('PriceRangeFacetComponent: Filter update detected (any filter type), scheduling robust slider range update', {
      filterType: 'any',
      eventType: event.type,
      hasDetail: !!event.detail
    });
    this.#scheduleRobustUpdate();
  };

  /**
   * Handles form change events (for filters that might not trigger FilterUpdate)
   * This is especially important for Search & Discovery app filters
   */
  #handleFormChange = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    
    // Only respond to filter inputs, not price range inputs (to avoid infinite loops)
    if (target.closest('price-range-facet-component')) return;
    
    console.log('PriceRangeFacetComponent: Form change detected, scheduling slider range update', {
      inputType: target.type,
      inputName: target.name,
      inputValue: target.value
    });
    this.#scheduleRobustUpdate();
  };

  /**
   * Handles form input events (for real-time filters)
   */
  #handleFormInput = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    
    // Only respond to filter inputs, not price range inputs (to avoid infinite loops)
    if (target.closest('price-range-facet-component')) return;
    
    // Debounce input events to avoid excessive updates
    if (this.#inputTimeoutId) {
      clearTimeout(this.#inputTimeoutId);
    }
    
    this.#inputTimeoutId = setTimeout(() => {
      console.log('PriceRangeFacetComponent: Form input detected, scheduling slider range update', {
        inputType: target.type,
        inputName: target.name,
        inputValue: target.value
      });
      this.#scheduleRobustUpdate();
    }, 300);
  };

  /**
   * Schedules a robust update that prevents race conditions
   */
  #scheduleRobustUpdate() {
    // Clear any existing timeout to prevent multiple updates
    if (this.#updateTimeoutId !== null) {
      clearTimeout(this.#updateTimeoutId);
    }
    
    // Use a single timeout that tries multiple strategies
    this.#updateTimeoutId = setTimeout(() => {
      this.#updateTimeoutId = null;
      this.#performRobustUpdate();
    }, 150);
  }

  /**
   * Performs a robust update with multiple fallback strategies
   */
  #performRobustUpdate() {
    if (this.#isUpdating) {
      console.log('PriceRangeFacetComponent: Update already in progress, skipping');
      return;
    }
    
    this.#isUpdating = true;
    console.log('PriceRangeFacetComponent: Starting robust update sequence');
    
    // Strategy 1: Immediate update
    this.#updateSliderRange();
    
    // Strategy 2: Animation frame update
    requestAnimationFrame(() => {
      this.#updateSliderRange();
      
      // Strategy 3: Double animation frame update
      requestAnimationFrame(() => {
        this.#updateSliderRange();
        
        // Strategy 4: Final timeout update
        setTimeout(() => {
          this.#updateSliderRange();
          this.#isUpdating = false;
          console.log('PriceRangeFacetComponent: Robust update sequence completed');
        }, 100);
      });
    });
  }

  /**
   * Ensures the slider range is updated using multiple timing strategies
   * to handle both initial load and AJAX re-renders
   */
  #ensureSliderRangeUpdate() {
    console.log('PriceRangeFacetComponent: #ensureSliderRangeUpdate called');
    
    // Use the robust update mechanism for consistency
    this.#scheduleRobustUpdate();
    
    // Also try immediate update for initial load
    requestAnimationFrame(() => {
      this.#updateSliderRange();
    });
  }

  /**
   * Sets up the range slider event listeners
   */
  #setupRangeSliders() {
    const { minRange, maxRange } = this.refs;

    if (minRange && maxRange) {
      minRange.addEventListener('input', this.#onMinRangeChange);
      maxRange.addEventListener('input', this.#onMaxRangeChange);
      minRange.addEventListener('change', this.#onRangeChangeComplete);
      maxRange.addEventListener('change', this.#onRangeChangeComplete);
    }
  }

  /**
   * Sets up mutation observer to watch for value changes and DOM updates
   * This catches changes from any source: theme filters, Search & Discovery apps, etc.
   */
  #setupMutationObserver() {
    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
    }
    
    this.#mutationObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'value' || 
             mutation.attributeName === 'data-range-max' ||
             mutation.attributeName === 'max' ||
             mutation.attributeName === 'min')) {
          shouldUpdate = true;
        }
        
        // Also watch for child list changes (new filters added by apps)
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          console.log('PriceRangeFacetComponent: DOM changes detected (possible new filters added)');
          shouldUpdate = true;
        }
      });
      
      if (shouldUpdate) {
        console.log('PriceRangeFacetComponent: Mutation detected, updating slider range');
        // Use requestAnimationFrame for smooth updates
        requestAnimationFrame(() => {
          this.#updateSliderRange();
        });
      }
    });

    // Watch for changes to the range inputs and the component itself
    const { minRange, maxRange } = this.refs;
    if (minRange && this.#mutationObserver) {
      this.#mutationObserver.observe(minRange, { 
        attributes: true, 
        attributeFilter: ['value', 'max', 'min'] 
      });
    }
    if (maxRange && this.#mutationObserver) {
      this.#mutationObserver.observe(maxRange, { 
        attributes: true, 
        attributeFilter: ['value', 'max', 'min'] 
      });
    }
    if (this.#mutationObserver) {
      this.#mutationObserver.observe(this, { 
        attributes: true, 
        attributeFilter: ['data-range-max'],
        childList: true,
        subtree: true
      });
    }
    
    // Also watch the parent form for any changes (Search & Discovery app filters)
    const form = this.closest('form');
    if (form && this.#mutationObserver) {
      this.#mutationObserver.observe(form, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'data-*']
      });
    }
  }


  /**
   * Handles minimum range slider input
   */
  #onMinRangeChange = () => {
    const { minRange, maxRange, minValueDisplay, minInput } = this.refs;
    if (!minRange || !maxRange) return;

    let minValue = parseInt(minRange.value, 10);
    let maxValue = parseInt(maxRange.value, 10);

    // Ensure min doesn't exceed max
    if (minValue > maxValue) {
      minValue = maxValue;
      minRange.value = minValue.toString();
    }

    // Update display
    if (minValueDisplay) {
      minValueDisplay.textContent = this.#formatValue(minValue);
    }

    // Update hidden input
    if (minInput) {
      minInput.value = minValue > 0 ? this.#formatValue(minValue) : '';
    }

    // Update visual range bar
    this.#updateSliderRange();
  };

  /**
   * Handles maximum range slider input
   */
  #onMaxRangeChange = () => {
    const { minRange, maxRange, maxValueDisplay, maxInput } = this.refs;
    if (!minRange || !maxRange) return;

    let minValue = parseInt(minRange.value, 10);
    let maxValue = parseInt(maxRange.value, 10);
    const rangeMax = parseInt(this.dataset.rangeMax || '0', 10);

    // Ensure max doesn't go below min
    if (maxValue < minValue) {
      maxValue = minValue;
      maxRange.value = maxValue.toString();
    }

    // Update display
    if (maxValueDisplay) {
      maxValueDisplay.textContent = this.#formatValue(maxValue);
    }

    // Update hidden input
    if (maxInput) {
      maxInput.value = maxValue < rangeMax ? this.#formatValue(maxValue) : '';
    }

    // Update visual range bar
    this.#updateSliderRange();
  };

  /**
   * Handles range change completion (triggers filter update)
   */
  #onRangeChangeComplete = () => {
    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSummary();
  };

  /**
   * Updates the visual range bar between the handles
   */
  #updateSliderRange() {
    const { minRange, maxRange, sliderRange, progressMin, progressMax } = this.refs;
    
    // Enhanced debugging
    console.log('PriceRangeFacetComponent: #updateSliderRange called', {
      hasMinRange: !!minRange,
      hasMaxRange: !!maxRange,
      hasSliderRange: !!sliderRange,
      minRangeValue: minRange?.value,
      maxRangeValue: maxRange?.value,
      rangeMax: this.dataset.rangeMax,
      sliderRangeComputedStyle: sliderRange ? window.getComputedStyle(sliderRange) : null,
      isUpdating: this.#isUpdating
    });
    
    if (!minRange || !maxRange || !sliderRange) {
      console.warn('PriceRangeFacetComponent: Missing required refs for slider range', {
        minRange: !!minRange,
        maxRange: !!maxRange,
        sliderRange: !!sliderRange
      });
      return;
    }

    // Check if elements are actually in the DOM and visible
    if (!minRange.isConnected || !maxRange.isConnected || !sliderRange.isConnected) {
      console.warn('PriceRangeFacetComponent: Elements not connected to DOM');
      return;
    }

    const rangeMax = parseInt(this.dataset.rangeMax || '0', 10);
    if (rangeMax === 0) {
      console.warn('PriceRangeFacetComponent: rangeMax is 0', { rangeMax: this.dataset.rangeMax });
      return;
    }

    const minValue = parseInt(minRange.value, 10) || 0;
    const maxValue = parseInt(maxRange.value, 10) || rangeMax;
    
    // Validate values
    if (isNaN(minValue) || isNaN(maxValue)) {
      console.warn('PriceRangeFacetComponent: Invalid min/max values', { minValue, maxValue });
      return;
    }

    // Calculate percentages
    const minPercent = Math.max(0, (minValue / rangeMax) * 100);
    const maxPercent = Math.min(100, (maxValue / rangeMax) * 100);

    console.log('PriceRangeFacetComponent: Calculating slider positions', {
      minValue,
      maxValue,
      rangeMax,
      minPercent,
      maxPercent,
      sliderRangeCurrentStyle: {
        left: sliderRange.style.left,
        right: sliderRange.style.right,
        width: sliderRange.style.width,
        display: sliderRange.style.display,
        visibility: sliderRange.style.visibility
      }
    });

    // Temporarily disable transitions to prevent CSS conflicts
    const originalTransition = sliderRange.style.transition;
    sliderRange.style.transition = 'none';
    
    // Update the visual range bar position and width
    sliderRange.style.left = minPercent + '%';
    sliderRange.style.right = (100 - maxPercent) + '%';
    sliderRange.style.width = (maxPercent - minPercent) + '%';
    
    // Ensure visibility - force these properties to override any CSS
    sliderRange.style.display = 'block';
    sliderRange.style.visibility = 'visible';
    sliderRange.style.opacity = '1';
    sliderRange.style.position = 'absolute';
    sliderRange.style.height = '100%';
    sliderRange.style.zIndex = '2';
    
    // Force a repaint to ensure styles are applied
    void sliderRange.offsetHeight;
    
    // Re-enable transitions after a brief delay
    requestAnimationFrame(() => {
      sliderRange.style.transition = originalTransition || 'left 0.15s ease, right 0.15s ease, width 0.15s ease';
    });

    // Update progress indicators to show "meeting" effect
    if (progressMin) {
      progressMin.style.width = minPercent + '%';
    }
    if (progressMax) {
      progressMax.style.width = (100 - maxPercent) + '%';
    }
    
    // Final validation - check if the slider range is actually visible
    const computedStyle = window.getComputedStyle(sliderRange);
    const isVisible = computedStyle.display !== 'none' && 
                     computedStyle.visibility !== 'hidden' && 
                     computedStyle.opacity !== '0' &&
                     sliderRange.offsetWidth > 0;
    
    console.log('PriceRangeFacetComponent: Slider range updated', {
      finalStyles: {
        left: sliderRange.style.left,
        right: sliderRange.style.right,
        width: sliderRange.style.width,
        display: sliderRange.style.display,
        visibility: sliderRange.style.visibility,
        opacity: sliderRange.style.opacity
      },
      computedStyle: {
        display: computedStyle.display,
        visibility: computedStyle.visibility,
        opacity: computedStyle.opacity,
        width: computedStyle.width
      },
      isVisible,
      offsetWidth: sliderRange.offsetWidth
    });
    
    // If not visible, try one more time after a brief delay
    if (!isVisible && !this.#isUpdating) {
      console.warn('PriceRangeFacetComponent: Slider range not visible after update, retrying');
      setTimeout(() => {
        this.#updateSliderRange();
      }, 50);
    }
  }

  /**
   * Formats a value for display (removes currency formatting)
   * @param {number} value - The value to format
   * @returns {string} The formatted value
   */
  #formatValue(value) {
    // Convert cents to decimal format
    return (value / 100).toFixed(2).replace(/\.00$/, '');
  }

  /**
   * Updates the price summary in the facet status
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    // Create temporary input elements with formatted values for the status component
    const tempMinInput = document.createElement('input');
    const tempMaxInput = document.createElement('input');
    
    tempMinInput.value = minInput?.value || '';
    tempMaxInput.value = maxInput?.value || '';

    statusComponent.updatePriceSummary(tempMinInput, tempMaxInput);
  }
}

if (!customElements.get('price-range-facet-component')) {
  customElements.define('price-range-facet-component', PriceRangeFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component, price-range-facet-component');
    
    // Handle price range facet component specifically
    if (container instanceof PriceRangeFacetComponent) {
      const { minRange, maxRange, minInput, maxInput, minValueDisplay, maxValueDisplay, sliderRange, progressMin, progressMax } = container.refs;
      const rangeMax = parseInt(container.dataset.rangeMax || '0', 10);
      
      // Reset range sliders
      if (minRange) minRange.value = '0';
      if (maxRange) maxRange.value = rangeMax.toString();
      
      // Reset hidden inputs
      if (minInput) minInput.value = '';
      if (maxInput) maxInput.value = '';
      
      // Reset displays
      if (minValueDisplay) minValueDisplay.textContent = '0';
      if (maxValueDisplay) maxValueDisplay.textContent = (rangeMax / 100).toFixed(2).replace(/\.00$/, '');
      
      // Reset visual range bar to full width (0% to 100%)
      if (sliderRange) {
        sliderRange.style.left = '0%';
        sliderRange.style.right = '0%';
        sliderRange.style.width = '100%';
        sliderRange.style.display = 'block';
        sliderRange.style.visibility = 'visible';
        sliderRange.style.opacity = '1';
      }
      
      // Reset progress indicators
      if (progressMin) progressMin.style.width = '0%';
      if (progressMax) progressMax.style.width = '0%';
    } else {
      // Handle regular inputs (checkbox and text-based price filter)
      container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
        if (input instanceof HTMLInputElement) {
          input.checked = false;
          input.value = '';
        }
      });
    }

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
